'use client'

import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  offset,
  Placement,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useListNavigation,
  useRole,
} from '@floating-ui/react-dom-interactions'
import React from 'react'

import { useEnhancedEffect, useLockBodyScroll } from '../../../hooks'
import { Arrow } from '../../../icons'
import { Input } from '../../Input'
import { Option } from './Option'
import { Container, Popup } from './styles'

export interface Option<T extends string | number | null | undefined> {
  label: React.ReactNode
  value: T
}

export interface SelectInputProps<T extends string | number | null | undefined>
  extends Omit<React.HTMLAttributes<HTMLSelectElement>, 'onChange' | 'onBlur'> {
  /**
   * Id attribute
   */
  id?: string

  /**
   * Name attribute
   */
  name?: string

  /**
   * Class name
   */
  className?: string

  /**
   * Select options
   */
  options: Option<T>[] | Readonly<Option<T>[]>

  /**
   * Value
   */
  value?: T

  /**
   * Placeholder
   */
  placeholder?: string

  /**
   * Has errors
   */
  hasError?: boolean

  /**
   * Disabled state
   */
  disabled?: boolean

  /**
   * onBlur handler
   */
  onBlur?: (e: React.FocusEvent) => void

  /**
   * onChange handler
   */
  onChange?: (value: T) => void

  /**
   * Auto-adjust option width
   */
  autoWidth?: boolean

  /**
   * Select placement
   */
  placement?: Placement

  /**
   * Disable body scroll lock
   */
  disableBodyScrollLock?: boolean
}

const SelectInputInner = <T extends string | number | undefined>(
  {
    id,
    className,
    name,
    value,
    options,
    placeholder,
    hasError = false,
    disabled = false,
    onBlur,
    onChange,
    autoWidth,
    placement = 'bottom',
    disableBodyScrollLock = false,
  }: SelectInputProps<T>,
  ref: React.ForwardedRef<HTMLInputElement>,
) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const listItemsRef = React.useRef<(HTMLLIElement | null)[]>([])

  const [open, setOpen] = React.useState(false)
  const [isInternalOpen, setInternalOpen] = React.useState(open)

  const [activeIndex, setActiveIndex] = React.useState<number | null>(null)
  const [pointer, setPointer] = React.useState(false)
  const [selectedIndex, setSelectedIndex] = React.useState<number>(
    options.findIndex((o) => o.value === value),
  )

  if (!open && pointer) {
    setPointer(false)
  }

  const label = React.useMemo(
    () => options.find((o) => o.value === value)?.label,
    [options, value],
  )

  React.useEffect(() => {
    if (!open) return
    setInternalOpen(open)
  }, [open])

  // Update selected index when value changes
  React.useEffect(() => {
    setSelectedIndex(options.findIndex((o) => o.value === value))
  }, [value, options])

  const handleCloseEnd = React.useCallback(() => {
    if (open) return
    setInternalOpen(false)
  }, [open])

  const handleOptionSelect = React.useCallback(
    (value: T, index: number) => {
      setSelectedIndex(index)
      onChange?.(value)
      setOpen(false)
      setActiveIndex(null)
    },
    [onChange],
  )

  const { x, y, reference, floating, strategy, context } = useFloating({
    open: isInternalOpen,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({ padding: 8 }),
      shift({
        padding: 8,
      }),
    ],
    placement,
  })

  const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions(
    [
      useClick(context),
      useRole(context, {
        role: 'listbox',
      }),
      useDismiss(context),
      useListNavigation(context, {
        listRef: listItemsRef,
        activeIndex,
        selectedIndex,
        onNavigate: setActiveIndex,
      }),
    ],
  )

  useLockBodyScroll(disableBodyScrollLock ? false : open)

  // Scroll the active or selected item into view.
  useEnhancedEffect(() => {
    if (!isInternalOpen || activeIndex === null || pointer) return
    requestAnimationFrame(() => {
      listItemsRef.current[activeIndex]?.scrollIntoView({
        block: 'nearest',
      })
    })
  }, [isInternalOpen, activeIndex, pointer])

  return (
    <Container ref={containerRef} className={className} autoWidth={autoWidth}>
      <Input
        variant="button"
        aria-labelledby={id}
        aria-haspopup="listbox"
        value={label}
        placeholder={placeholder}
        open={open}
        disabled={disabled}
        autoWidth={autoWidth}
        error={hasError}
        iconAfter={<Arrow direction="down" />}
        onBlur={onBlur}
        {...getReferenceProps({
          ref: reference,
        })}
      />

      {isInternalOpen && options.length ? (
        <FloatingFocusManager context={context}>
          <Popup
            role="listbox"
            id={`${id}_list`}
            tabIndex={-1}
            open={open}
            onAnimationEnd={handleCloseEnd}
            {...getFloatingProps({
              ref: floating,
              style: {
                zIndex: 1,
                position: strategy,
                top: 0,
                left: 0,
                width: autoWidth ? 'auto' : '100%',
                transform: `translate(${Math.round(x ?? 0)}px,${Math.round(
                  y ?? 0,
                )}px)`,
              },
              onPointerMove() {
                setPointer(true)
              },
              onKeyDown(event) {
                setPointer(false)

                if (event.key === 'Tab') {
                  setOpen(false)
                }
              },
            })}
          >
            {options.map(({ label, value }, index) => (
              <Option<T>
                key={index}
                ref={(node) => {
                  listItemsRef.current[index] = node
                }}
                tabIndex={activeIndex === index ? 0 : 1}
                active={activeIndex === index}
                selected={selectedIndex === index}
                value={value}
                {...getItemProps({
                  onClick: () => handleOptionSelect(value, index),
                  onKeyDown: (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleOptionSelect(value, index)
                    }
                  },
                })}
              >
                {label}
              </Option>
            ))}
          </Popup>
        </FloatingFocusManager>
      ) : null}

      {name && value !== undefined && (
        <input type="hidden" name={name} value={value} ref={ref} />
      )}
    </Container>
  )
}

export const SelectInput = React.forwardRef(SelectInputInner) as <
  T extends string | number | undefined,
>(
  props: SelectInputProps<T> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof SelectInputInner>
